// In-memory access-token holder — mirrors the Sales app's own `token.ts`
// exactly (MIHIR-RESPONSE-web-token-mechanism.md §Q3, reference file
// confirmed by Rudra). The token NEVER touches localStorage/sessionStorage;
// it lives only in this module's memory for the lifetime of the page,
// refreshed by sso-client.ts shortly before it expires.

import { DEV_TOKEN } from '@/config/env';

let accessToken: string | null = null;

// Tokens this app minted and has since replaced. Needed because a refresh can
// land BETWEEN a stale request going out and its 401 coming back: with two
// parallel host actions, the first triggers the refresh and the second's token
// is then neither current nor recognisable, so it would be refused a retry and
// fail for no reason. Bounded — this is a short recency window, not a history.
const MAX_RECENT = 4;
const recentTokens: string[] = [];

/**
 * Was this token issued by OUR SSO session (current, or recently superseded)?
 *
 * The http client asks before retrying a 401. A Sales-embedded host's token
 * arrives via the URL fragment and never passes through setAccessToken, so it
 * is never "ours" — which is correct: Meet holds no refresh cookie for that
 * session, and replaying its request with the SSO token would silently swap
 * one identity for another.
 */
export function isOwnAccessToken(token: string): boolean {
  if (!token) return false;
  if (getAccessToken() === token) return true;
  return recentTokens.includes(token);
}

/**
 * Local-dev bypass (MIHIR-RESPONSE-web-token-mechanism.md §Q4): when
 * NEXT_PUBLIC_DEV_TOKEN is set, always return it directly — the real
 * cross-subdomain cookie flow only works once Meet is served from a real
 * `.app.growithnsi.com` host, so local/preview dev uses this instead.
 */
export function getAccessToken(): string | null {
  return DEV_TOKEN ?? accessToken;
}

export function setAccessToken(token: string | null): void {
  if (accessToken && accessToken !== token) {
    recentTokens.push(accessToken);
    if (recentTokens.length > MAX_RECENT) recentTokens.shift();
  }
  accessToken = token;
}

export interface CurrentUser {
  fullName: string | null;
  phone: string | null;
}

let currentUser: CurrentUser | null = null;

/** Set alongside the access token by sso-client.ts's refresh — the `/auth/refresh`
 *  response body carries a `user` object (fullName, phone, ...) the JWT itself
 *  doesn't (see jwt.strategy.ts: name/email are never on the access token). */
export function setCurrentUser(user: CurrentUser | null): void {
  currentUser = user;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * The signed-in user for display (header profile menu). Prefers the `user`
 * object captured from `/auth/refresh` (has fullName); falls back to
 * decoding the access token's own claims when only a token is available
 * (the host-injected mobile-app WebView path, token.ts's isHostInjected —
 * the JWT has `phone` but never `fullName`, so that field stays null there).
 */
export function getDisplayUser(): CurrentUser | null {
  if (currentUser) return currentUser;
  const token = getAccessToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    fullName: typeof payload.name === 'string' ? payload.name : null,
    phone: typeof payload.phone === 'string' ? payload.phone : null,
  };
}

/**
 * True when this page is running inside our own mobile app's WebView
 * (`react-native-webview` injects `window.ReactNativeWebView` automatically
 * — nothing else sets this). In that context the native app already holds a
 * real, independently-refreshed SSO session (application/services/auth.ts)
 * and owns the identity handoff — this page must NOT attempt the
 * cookie-based flow in sso-client.ts, since a WebView's cookie jar is
 * separate from the native app and from external Chrome (the exact
 * "logs in, then just sits in Chrome" bug this replaces).
 */
export function isHostInjected(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!(window as unknown as { ReactNativeWebView?: unknown }).ReactNativeWebView
  );
}

/**
 * The native app's side of the handoff: calls this (via `injectJavaScript`)
 * with its own current access token, once on load and again on every native
 * refresh — application/components/AppWebView.tsx. Firing a DOM event
 * (rather than the native side reaching into React state directly) lets
 * useAuthBootstrap react immediately without polling.
 */
declare global {
  interface Window {
    __NSI_MEET_SET_TOKEN__?: (token: string | null) => void;
  }
}

if (typeof window !== 'undefined') {
  window.__NSI_MEET_SET_TOKEN__ = (token: string | null) => {
    setAccessToken(token);
    window.dispatchEvent(new CustomEvent<string | null>('nsimeet:token', { detail: token }));
  };
}
